import type { CalculatorContent, Faq } from '@/types/calculator'
import {
  brackets2027,
  medicalCredits2027,
  rebates2027,
  retirement2027,
  uif2027,
} from '@/data/tax/2027'
import type { PayeInputs } from './types'

const lowestRate = brackets2027[0].rate
const topRate = brackets2027[brackets2027.length - 1].rate
const taxThreshold = rebates2027.primary / lowestRate

export const content: CalculatorContent<PayeInputs> = {
  intro:
    'Use this salary calculator for South Africa to estimate your take-home pay after tax. It applies the SARS PAYE tax tables, UIF and medical scheme tax credits for the current tax year, so you can see what lands in your bank account each month.',
  howItWorks: [
    `Your gross pay is annualised (monthly figures are multiplied by 12) and any retirement-fund contribution is deducted, capped at ${retirement2027.deductionRate * 100}% of taxable income or R${retirement2027.annualCap.toLocaleString('en-ZA')} per year, whichever is lower.`,
    `The remaining taxable income is run through the SARS progressive tax brackets — marginal rates from ${lowestRate * 100}% to ${topRate * 100}% — to give tax before credits. The primary rebate of R${rebates2027.primary.toLocaleString('en-ZA')} is then subtracted, plus the secondary and tertiary rebates if you are 65 or older.`,
    `If you belong to a medical scheme, the monthly medical tax credits (R${medicalCredits2027.mainMember} for the main member and first dependant, R${medicalCredits2027.additionalDependant} for each additional dependant) reduce the annual tax further. Finally, employee UIF at ${uif2027.employeeRate * 100}% of gross pay, capped at a monthly remuneration ceiling of R${uif2027.monthlyCap.toLocaleString('en-ZA')}, is deducted.`,
    'The result shows your estimated annual and monthly PAYE, UIF, deductions and net take-home pay. It is an indicative figure — payroll systems also account for fringe benefits, bonuses and year-to-date income.',
  ],
  formulas: [
    {
      label: 'Taxable income',
      expression: 'annual gross − retirement deduction',
      notes: `Retirement deduction capped at ${retirement2027.deductionRate * 100}% of income or R${retirement2027.annualCap.toLocaleString('en-ZA')} a year.`,
    },
    {
      label: 'Tax before credits',
      expression: 'bracket base + bracket rate × (taxable income − bracket minimum)',
    },
    {
      label: 'Annual PAYE',
      expression: 'tax before credits − rebates − medical tax credits',
      notes: `Primary rebate R${rebates2027.primary.toLocaleString('en-ZA')}; secondary R${rebates2027.secondary.toLocaleString('en-ZA')} (65+); tertiary R${rebates2027.tertiary.toLocaleString('en-ZA')} (75+).`,
    },
    {
      label: 'Employee UIF',
      expression: `${uif2027.employeeRate * 100}% × monthly gross, capped at R${uif2027.monthlyCap.toLocaleString('en-ZA')} remuneration`,
    },
    {
      label: 'Net take-home pay',
      expression: 'gross pay − PAYE − UIF − retirement contribution',
    },
  ],
  example: {
    title: 'Monthly salary of R25 000, under 65, no deductions',
    description:
      'A typical South African employee earning a monthly gross salary with no retirement contribution and no medical scheme membership.',
    inputs: {
      grossPay: 25_000,
      grossPayPeriod: 'monthly',
      ageBand: 'under65',
      pensionContribution: 0,
      pensionPeriod: 'monthly',
      medicalSchemeMember: false,
      medicalDependants: 0,
    },
  },
  definitions: [
    {
      term: 'PAYE',
      description:
        'Pay-As-You-Earn: income tax your employer withholds from your salary and pays over to SARS each month.',
    },
    {
      term: 'UIF',
      description: `Unemployment Insurance Fund contribution. Employees pay ${uif2027.employeeRate * 100}% of remuneration up to a monthly ceiling; employers pay a matching contribution.`,
    },
    {
      term: 'Rebate',
      description:
        'A fixed amount that reduces your annual tax. Everyone gets the primary rebate; taxpayers 65+ and 75+ get additional rebates.',
    },
    {
      term: 'Medical scheme fees tax credit',
      description:
        'A monthly credit that reduces PAYE for medical scheme members, based on the number of dependants.',
    },
    {
      term: 'Tax threshold',
      description: `The annual income below which no tax is payable — currently about R${Math.round(taxThreshold).toLocaleString('en-ZA')} for taxpayers under 65.`,
    },
    {
      term: 'Effective tax rate',
      description:
        'Your total PAYE divided by gross income — usually much lower than your marginal bracket rate.',
    },
  ],
}

export const faqs: Faq[] = [
  {
    question: 'Does this give an exact PAYE figure?',
    answer:
      'No. This is an estimate based on the SARS tax tables and common deductions. Individual employer payroll systems, fringe benefits and other deductions can change the result.',
  },
  {
    question: 'What is the UIF limit used?',
    answer: `Employee UIF is calculated at ${uif2027.employeeRate * 100}% of gross remuneration, capped at the monthly earnings ceiling of R${uif2027.monthlyCap.toLocaleString('en-ZA')}.`,
  },
  {
    question: 'How much tax do I pay on my salary in South Africa?',
    answer:
      'It depends on your taxable income after deductions. The PAYE calculator applies the SARS brackets and rebates to show your estimated annual and monthly tax.',
  },
  {
    question: 'Does a pension contribution reduce my PAYE?',
    answer: `Yes. Retirement-fund contributions are deductible up to ${retirement2027.deductionRate * 100}% of taxable income (capped at R${retirement2027.annualCap.toLocaleString('en-ZA')} a year), which lowers the tax withheld.`,
  },
  {
    question: 'Is take-home pay after tax the same as net salary?',
    answer:
      'Yes. Take-home (net) pay is what remains after PAYE, UIF and your deductions such as retirement contributions are subtracted from gross pay.',
  },
]
